// SelectProjectPage — the project chooser (the shell's slim frame, reached via
// resolveLanding case 3: more than one usable project and no usable last one).
// The viewer's projects on the ONE project row (`projectRows` — the same rows
// the switcher's menu draws: the dot · the name · "<type> · <n> active sites" ·
// a chip when blocked or on trial), A–Z, the family's search box past 8 with
// the shared matching rules. Reachable rows enter; suspended / expired-trial
// rows route to /project-blocked (so they never silently vanish on a member);
// terminated projects are hidden — they live only in the platform portal. The
// list is uniform — a sysadmin sees the same non-terminated shape (ADR-022);
// their platform door is the slim bar's gear.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useMe } from '../hooks/useMe.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';
import { Spinner } from '../components/ui/Spinner.js';
import { PickerSearchBox } from '../components/ui/PickerList.js';
import { SHORT_LIST } from '../hooks/useOptions.js';
import { matchProjectRows, projectRows, ProjectRowFace } from '../components/layout/projectRows.js';

export function SelectProjectPage() {
  const { t, i18n } = useTranslation(['common', 'nav']);
  const { data: me, isLoading } = useMe();
  const [now] = useState(() => Date.now());
  const [text, setText] = useState('');
  useDocumentTitle(t('common:selectProject.title'));
  const rows = useMemo(() => (me ? projectRows(me, null, now, t, i18n.language) : []), [me, now, t, i18n.language]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div role="status" aria-live="polite">
          <Spinner size={32} className="text-[var(--color-text-secondary)]" />
          <span className="sr-only">{t('common:statuses.loading')}</span>
        </div>
      </div>
    );
  }

  if (!me) return null;

  const shown = text ? matchProjectRows(rows, text) : rows;
  const q = text.trim();
  // The list's word when it has no rows — nothing matches the typed text · nothing to choose from at all.
  const notice = shown.length > 0 ? '' : q ? t('common:picker.noResults', { q }) : t('common:picker.empty');

  return (
    <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-8">
      <header className="mb-6 space-y-1">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{t('common:selectProject.title')}</h1>
        <p className="text-sm text-[var(--color-text-secondary)]">{t('common:selectProject.subtitle')}</p>
      </header>

      {rows.length > SHORT_LIST && (
        <div className="mb-3">
          <PickerSearchBox value={text} onChange={setText} placeholder={t('nav:switcher.search')} />
        </div>
      )}

      {/* A live region that stays mounted (SC 4.1.3): the word is announced when the list empties, silent otherwise. */}
      <div role="status" aria-live="polite" className={notice ? 'px-3 py-2 text-sm text-[var(--color-text-secondary)]' : undefined}>
        {notice}
      </div>
      {shown.length > 0 && (
        <ul className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)]" aria-label={t('common:selectProject.title')}>
          {shown.map((row) => (
            <li key={row.id}>
              {/* A ROW that navigates — the router's link with the row's own look (the hover fill, no underline: the whole row is the target), not the inline text `Link` (info-coloured, underlined on hover). */}
              <RouterLink to={row.to} className="flex items-center gap-3 px-4 py-3 outline-none transition-colors hover:bg-[var(--color-bg-hover)] focus-visible:bg-[var(--color-bg-hover)] focus-ring-inset pointer-coarse:min-h-14">
                <ProjectRowFace row={row} />
              </RouterLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SelectProjectPage;
